import {
  useRef,
  useEffect,
  useState,
  FC,
  MouseEvent as ReactMouseEvent,
} from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { setDataToLocalStorage } from 'src/localStorage/setDataToLocalStorage'
import { LocalStorageKey } from 'src/localStorage/enums/localStorageKey.enum'
import { getDataFromLocalStorage } from 'src/localStorage/getDataFromLocalStorage'
import CustomLink from 'src/components/CustomLink'
import { ProjectsPlaceholderModal } from 'src/components/ProjectsPlaceholderModal/index'
import { useGetSelfQuery } from 'src/reactQuery/useGetSelfQuery'
import { useGetProjectsQuery } from 'src/reactQuery/useGetProjectsQuery'
import { HOME_ROUTE } from 'src/constants/routes'
import classes from './sidebar.module.scss'
import { SIDEBAR_ITEMS } from './constants'

interface SidebarProps {
  // When true (a banner is showing), feature groups collapse their sub-pages
  // and behave as a single-open accordion — clicking a group label reveals
  // only that group's sub-pages.
  collapseGroups?: boolean
}

export const Sidebar: FC<SidebarProps> = ({ collapseGroups = false }) => {
  const container = useRef(null)

  const location = useLocation()

  // Index of the group label whose sub-pages are currently expanded (accordion
  // is single-open). Only used while collapseGroups is true.
  const [expandedGroup, setExpandedGroup] = useState<number | null>(null)

  function toggleGroup(groupIndex: number): void {
    setExpandedGroup((prev) => (prev === groupIndex ? null : groupIndex))
  }

  const { data: userData } = useGetSelfQuery()
  const { data: projectsResponse } = useGetProjectsQuery()

  const hasAdminAccess = userData?.user?.role === 'admin'
  const projects = projectsResponse?.data || []

  const lastActiveIndexString = getDataFromLocalStorage(
    LocalStorageKey.LAST_ACTIVE_INDEX
  )
  const lastActiveIndex = Number(lastActiveIndexString)

  const [activeIndex, setActiveIndex] = useState(lastActiveIndex)
  const [isProjectsModalOpen, setIsProjectsModalOpen] = useState(false)

  function changeActiveIndex(newIndex: number): void {
    setDataToLocalStorage(
      LocalStorageKey.LAST_ACTIVE_INDEX,
      newIndex.toString()
    )

    setActiveIndex(newIndex)
  }

  function getPath(path: string): string {
    if (path.charAt(0) !== '/') {
      return `/${path}`
    }
    return path
  }

  useEffect(() => {
    const activeItem = SIDEBAR_ITEMS.findIndex((item: any) => {
      if (!item.route) return false
      const itemPath = getPath(item.route)
      const currentPath = getPath(location.pathname)
      // Check if current path starts with the item route (for nested routes)
      return currentPath === itemPath || currentPath.startsWith(itemPath + '/')
    })
    changeActiveIndex(activeItem)
  }, [location])

  const handleProjectsNavigation = (
    event: ReactMouseEvent<HTMLAnchorElement>
  ) => {
    if (projects.length > 0) {
      return
    }

    event.preventDefault()
    setIsProjectsModalOpen(true)
  }

  return (
    <div className={classes.sidebarContainer} ref={container}>
      <CustomLink className={classes.sidebarHeader} link={HOME_ROUTE}>
        <img alt="logo" className={classes.sidebarLogo} src="/logo.png" />
      </CustomLink>

      <div className={classes.sidebarMenu}>
        {SIDEBAR_ITEMS.map((item: any, index) => {
          const {
            customIcon: CustomIcon,
            hasDivider,
            icon,
            isGroupLabel,
            isNested,
            isPlaceholder,
            isFeaturesLabel,
            name,
            requiresAdminAccess,
            route,
          } = item
          if (requiresAdminAccess && !hasAdminAccess) return null

          // Group label (non-clickable)
          if (isGroupLabel) {
            // Find all nested items that follow this group
            const nestedItems = []
            let nextIndex = index + 1
            while (
              nextIndex < SIDEBAR_ITEMS.length &&
              (SIDEBAR_ITEMS[nextIndex] as any).isNested
            ) {
              nestedItems.push(SIDEBAR_ITEMS[nextIndex])
              nextIndex++
            }

            // Check if any nested item is active
            const isAnyNestedActive = nestedItems.some(
              (_, nestedIdx) => activeIndex === index + nestedIdx + 1
            )

            // When a banner is showing we collapse groups; sub-pages are
            // revealed only for the group the user toggled open.
            const isExpanded = !collapseGroups || expandedGroup === index

            return (
              <div key={name}>
                <div
                  className={classNames(
                    classes.sidebarGroupLabel,
                    isAnyNestedActive && classes.active,
                    collapseGroups && classes.collapsible
                  )}
                  onClick={
                    collapseGroups ? () => toggleGroup(index) : undefined
                  }
                  role={collapseGroups ? 'button' : undefined}
                  tabIndex={collapseGroups ? 0 : undefined}
                >
                  {CustomIcon && (
                    <span className={classes.sidebarMenuItemIconWrapper}>
                      <CustomIcon className={classes.sidebarMenuItemIcon} />
                    </span>
                  )}
                  <span className={classes.sidebarMenuItemName}>{name}</span>
                  {collapseGroups && nestedItems.length > 0 && (
                    <svg
                      className={classNames(
                        classes.groupChevron,
                        isExpanded && classes.groupChevronOpen
                      )}
                      fill="none"
                      height="16"
                      viewBox="0 0 24 24"
                      width="16"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 9l6 6 6-6"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                  )}
                </div>
                {nestedItems.length > 0 && (
                  <div
                    className={classNames(
                      classes.nestedGroupWrap,
                      !isExpanded && classes.collapsed
                    )}
                  >
                    <div className={classes.nestedGroupInner}>
                      <div className={classes.nestedGroup}>
                        {nestedItems.map((nestedItem: any, nestedIdx) => (
                          <CustomLink
                            className={classNames(
                              classes.sidebarMenuItem,
                              classes.nested,
                              activeIndex === index + nestedIdx + 1 &&
                                classes.active
                            )}
                            key={nestedItem.name}
                            link={nestedItem.route || '#'}
                          >
                            <span className={classes.sidebarMenuItemName}>
                              {nestedItem.name}
                            </span>
                          </CustomLink>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {nestedItems.length > 0 &&
                  (nestedItems[nestedItems.length - 1] as any).hasDivider && (
                    <div className={classes.sidebarDivider} />
                  )}
              </div>
            )
          }

          // Skip nested items as they're rendered with their group
          if (isNested) return null

          if (isFeaturesLabel) {
            return (
              <div className={classes.featuresLabel} key={name}>
                {name}
              </div>
            )
          }

          // Regular item
          const isHome = name === 'Home'
          const isProjects = name === 'Projects'
          const isTopNavigationItem = isHome || isProjects
          const isActive = activeIndex === index
          if (isPlaceholder) {
            return (
              <div key={`${name}-wrapper`}>
                <div
                  className={classNames(
                    classes.sidebarMenuItem,
                    classes.placeholder,
                    classes.projectsItem
                  )}
                >
                  <span className={classes.sidebarMenuItemIconWrapper}>
                    {CustomIcon ? (
                      <CustomIcon className={classes.sidebarMenuItemIcon} />
                    ) : icon ? (
                      <FontAwesomeIcon
                        className={classes.sidebarMenuItemIcon}
                        icon={icon}
                      />
                    ) : null}
                  </span>
                  <span className={classes.sidebarMenuItemName}>{name}</span>
                </div>
                {hasDivider && (
                  <div
                    className={classes.sidebarDivider}
                    key={`${name}-divider`}
                  />
                )}
              </div>
            )
          }

          return (
            <>
              <CustomLink
                className={classNames(
                  classes.sidebarMenuItem,
                  isActive && classes.active,
                  isTopNavigationItem && classes.topNavigationItem,
                  isTopNavigationItem && !isActive && classes.inactiveTopItem,
                  isTopNavigationItem && isActive && classes.homeActive,
                  isHome && classes.homeItem,
                  isProjects && classes.projectsItem,
                  requiresAdminAccess && classes.bottom
                )}
                key={name}
                link={route || '#'}
                onClick={isProjects ? handleProjectsNavigation : undefined}
              >
                <span className={classes.sidebarMenuItemIconWrapper}>
                  {CustomIcon ? (
                    <CustomIcon className={classes.sidebarMenuItemIcon} />
                  ) : icon ? (
                    <FontAwesomeIcon
                      className={classes.sidebarMenuItemIcon}
                      icon={icon}
                    />
                  ) : null}
                </span>

                <span className={classes.sidebarMenuItemName}>{name}</span>
              </CustomLink>
              {hasDivider && (
                <div
                  className={classes.sidebarDivider}
                  key={`${name}-divider`}
                />
              )}
            </>
          )
        })}
      </div>

      <ProjectsPlaceholderModal
        isOpen={isProjectsModalOpen}
        onClose={() => setIsProjectsModalOpen(false)}
      />
    </div>
  )
}
